import {
  Directive,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { PermissionsService, PermissionTypes } from "./permissions.service";
import { Subscription } from "rxjs";

type ChildPermission = {
  submoduleId: number;
  type: PermissionTypes;
  key_identity?: string | string[];
};

@Directive({
  selector: "[appHasPermission]",
  standalone: true
})
export class HasPermissionDirective implements OnInit, OnChanges, OnDestroy {

  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private _permissionsService = inject(PermissionsService);
  private router = inject(Router);

  /**
   * Configura el tipo de permiso para el submódulo o si es para otro submódulo
   * @example { submoduleId: 1, type: 'isRead|isWrite|isCreate|isDelete|isSpecial' }
   */
  @Input("appHasPermission") permission: ChildPermission | string;
  private permissionSub: Subscription | null = null;
  permissionType: PermissionTypes;
  private lastRenderedState: { submoduleId?: number; permissionType?: number; visible?: boolean } = {};

  ngOnInit() {
    this.permissionSub = this._permissionsService.onPermissionsChange()
      .subscribe((changedId) => {
        const child = (this.permission as ChildPermission);
        const submoduleId = child?.submoduleId ?? this._permissionsService.getActiveRouteId();
        if (changedId !== null && changedId !== submoduleId) return;

        this.roleHasPermission();
      });

    this.roleHasPermission();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Si cambia el input de la directiva, re-evaluar
    if (changes["permission"]) {
      this.roleHasPermission();
    }
  }

  ngOnDestroy() {
    this.permissionSub?.unsubscribe();
  }

  getPermissionTypeId(permissionTypeName) {
    switch (permissionTypeName) {
      case "isRead":
        return 1;
      case "isWrite":
        return 2;
      case "isCreate":
        return 3;
      case "isDelete":
        return 4;
      case "isSpecial":
        return 5;
      default:
        return null;
    }
  }  
  
  /**
   * Verifica si el rol del usuario actual tiene permisos para realizar la acción
   */
  roleHasPermission() {
    const childRaw = (this.permission as any);
    // admitir dos formatos:
    // 1) objeto: { submoduleId: X, type: 'isSpecial', key_identity: 'sp_est' }
    // 2) string: "isSpecial:sp_est" o "isWrite"
    let typeInput: string | undefined;
    let specialKeys: string[] = [];
    let submoduleIdFromInput: number | undefined;

    if (typeof childRaw === 'string') {
      // formato "isSpecial:sp_est" o "isWrite"
      const parts = childRaw.split(':');
      typeInput = parts[0];
      specialKeys = parts[1] ? [parts[1]] : [];
    } else if (typeof childRaw === 'object' && childRaw !== null) {
      typeInput = childRaw.type;
      if (Array.isArray(childRaw.key_identity)) {
        specialKeys = childRaw.key_identity.filter((key) => typeof key === "string" && key.trim().length > 0);
      } else if (typeof childRaw.key_identity === "string" && childRaw.key_identity.trim().length > 0) {
        specialKeys = [childRaw.key_identity];
      }
      submoduleIdFromInput = childRaw.submoduleId;
    }

    const requestedType = this.getPermissionTypeId(typeInput);
    if (!requestedType) {
      this.updateView(false, null, null);
      return;
    }

    const serviceActiveSubmodule = this._permissionsService.getActiveRouteId();
    const routePermissionSubmodule = this.getCurrentRoutePermissionId();
    const submoduleId = submoduleIdFromInput ?? serviceActiveSubmodule ?? routePermissionSubmodule;
    if (submoduleId == null) {
      this.updateView(false, submoduleId, requestedType);
      return;
    }

    let hasPermission = false;
    if (requestedType === 5) {
      // permiso especial: validar una o varias key_identity (OR)
      hasPermission = specialKeys.some((key) => this._permissionsService.hasSpecialPermission(key, submoduleId));
    } else {
      hasPermission = this._permissionsService.hasPermissionTo(requestedType, submoduleId);
    }

    this.updateView(hasPermission, submoduleId, requestedType);
  }

  /**
   * Obtiene el `data.permission` de la ruta más profunda activa.
   * Sirve como respaldo cuando activeRouteId no está sincronizado.
   */
  private getCurrentRoutePermissionId(): number | null {
    let current: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (current?.firstChild) {
      current = current.firstChild;
    }

    const rawPermission = current?.data?.['permission'];
    const parsedPermission = Number(rawPermission);
    return Number.isFinite(parsedPermission) ? parsedPermission : null;
  }

  /**
   * Actualiza la visibilidad del template
   * @param visible - Visibilidad
   * @param submoduleId - Id del submódulo
   * @param permissionType - Tipo de permiso
   * @returns 
   */
  private updateView(visible: boolean, submoduleId: number | null, permissionType: number | null) {
    // evita re-render si nada cambió
    if (
      this.lastRenderedState.submoduleId === submoduleId &&
      this.lastRenderedState.permissionType === permissionType &&
      this.lastRenderedState.visible === visible
    ) return;

    this.lastRenderedState = { submoduleId: submoduleId ?? undefined, permissionType: permissionType ?? undefined, visible };

    if (visible) {
      if (this.viewContainer.length === 0) this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      this.viewContainer.clear();
    }
  }
}